import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, limit, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import PostCard from '../components/posts/PostCard';
import TrendingHashtagsSidebar from '../components/sidebar/TrendingHashtagsSidebar';

interface Post {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  likes: number;
  comments: number;
  createdAt: any;
}

const Explore: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'trending' | 'recent' | 'media'>('trending');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        let postsQuery;

        if (activeTab === 'trending') {
          // For trending, order by likes (most popular)
          postsQuery = query(
            collection(db, 'posts'),
            orderBy('likes', 'desc'),
            limit(20)
          );
        } else if (activeTab === 'recent') {
          // For recent, order by creation date
          postsQuery = query(
            collection(db, 'posts'),
            orderBy('createdAt', 'desc'),
            limit(20)
          );
        } else if (activeTab === 'media') {
          // For media, filter posts with images or videos
          postsQuery = query(
            collection(db, 'posts'),
            where('hasMedia', '==', true),
            orderBy('createdAt', 'desc'),
            limit(20)
          );
        }

        if (postsQuery) {
          const querySnapshot = await getDocs(postsQuery);
          const fetchedPosts: Post[] = [];

          querySnapshot.forEach((doc) => {
            fetchedPosts.push({
              id: doc.id,
              ...doc.data() as Omit<Post, 'id'>
            });
          });

          setPosts(fetchedPosts);
        }
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [activeTab]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Main content */}
      <div className="lg:col-span-8 space-y-4">
        <h1 className="text-2xl font-bold mb-4">Explorar</h1>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('trending')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'trending'
                  ? 'border-domired-500 text-domired-600 dark:text-domired-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              Tendencias
            </button>
            <button
              onClick={() => setActiveTab('recent')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'recent'
                  ? 'border-domired-500 text-domired-600 dark:text-domired-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              Recientes
            </button>
            <button
              onClick={() => setActiveTab('media')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'media'
                  ? 'border-domired-500 text-domired-600 dark:text-domired-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              Imágenes y Videos
            </button>
          </nav>
        </div>

        {loading ? (
          <div className="flex justify-center my-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-domired-600"></div>
          </div>
        ) : posts.length > 0 ? (
          <div className="space-y-4">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg shadow">
            <p className="text-gray-500 dark:text-gray-400">
              No hay publicaciones para mostrar en esta categoría.
            </p>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="lg:col-span-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sticky top-4">
          <TrendingHashtagsSidebar />
        </div>
      </div>
    </div>
  );
};

export default Explore;
